/**
 * The deck renderer. Pure layout: stack vs grid. No choreography state, no
 * timers, no playback refs. All animation behavior is driven by per-card
 * status in the controller and computed by `deckAnimate`.
 *
 * View mode toggles do not remount this component — the motion-snapshot bug
 * the v1 list worked around with double-rAF + a layout-pass counter cannot
 * occur here.
 */
import { LayoutGroup } from "motion/react";
import { useCallback } from "react";
import { match } from "ts-pattern";

import { cn } from "@/web/lib/styling";
import {
  useCompleteTourStep,
  useTourStep,
} from "@/web/routes/_protected/smb/-dashboard/-acknowledgments/useTourStep";
import {
  STACK_CARD_SIZE_CLASS,
  type ViewMode,
} from "@/web/routes/_protected/smb/-dashboard/-deckAnimate";
import { DeckCard } from "@/web/routes/_protected/smb/-dashboard/-DeckCard";
import type { CardDeckController } from "@/web/routes/_protected/smb/-dashboard/-useCardDeck";

export function CardDeck({
  controller,
  viewMode,
  className,
}: {
  controller: CardDeckController;
  viewMode: ViewMode;
  className?: string;
}) {
  const {
    cards,
    passPosition,
    liveCount,
    skip,
    complete,
    recycle,
    beginSwipePending,
    resolveSwipe,
    onExitAnimationComplete,
  } = controller;

  const tourRef = useTourStep("swipeToSkip");
  const completeSwipeToSkipStep = useCompleteTourStep("swipeToSkip");

  // Skipping a card is the exact action the swipe-to-skip step is describing,
  // so performing it retires that step. Wired here rather than on the gesture
  // alone so the Skip button dismisses it too — either way the user has just
  // demonstrated they understood the instruction.
  const skipAndAdvanceTour = useCallback(
    (id: string) => {
      completeSwipeToSkipStep();
      skip(id);
    },
    [completeSwipeToSkipStep, skip],
  );

  return (
    <LayoutGroup id="smb-card-deck-v2">
      <div
        // Anchor for the tour's swipe-to-skip step. The container, not the
        // front card — the front card's identity changes as cards are skipped,
        // so a card-level anchor would unmount out from under the tooltip.
        ref={tourRef}
        data-slot="smb-card-deck"
        data-view-mode={viewMode}
        className={cn(
          // "overflow-visible",
          match(viewMode)
            .with(
              "stack",
              () =>
                // No `min-h` — the deck shares available vertical space
                // with the header and footer via `flex-1`, and the card's
                // own `100cqh`-based height calc keeps it fitted on short
                // viewports. A `min-h` here forces overflow on small
                // phones, which manifests as the stack overlapping the
                // page header.
                "@container/deck relative isolate flex flex-1 items-center justify-center @container-[size] perspective-distant",
            )
            // Grid also gets `@container/deck @container-[size]` so cards
            // can use the same `cqh`/`cqw` height calc as in stack mode.
            // Without matching sizing, the stack↔grid toggle visibly
            // resizes the cards mid-animation. `overflow-y-auto` keeps
            // multiple cards scrollable within the deck rather than
            // expanding the page.
            .with(
              "grid",
              () => "@container/deck flex flex-1 flex-wrap content-start justify-center gap-6 pt-1",
            )
            .exhaustive(),
          className,
        )}
      >
        {(() => {
          // Index assignment: exiting cards keep an "anchor" index (front of
          // stack) so their peel/fade animates from a sensible baseline. The
          // remaining cards count themselves as the new live deck, so their
          // animate targets snap to the post-exit positions immediately —
          // that's what gives the "stack moves up while the front fades"
          // behavior with no separate coordinator.
          const liveTotal = cards.reduce(
            (n, c) =>
              n + (c.status.kind === "exit-skip" || c.status.kind === "exit-complete" ? 0 : 1),
            0,
          );
          let liveCounter = 0;
          // When only one live card remains, suppress its skip affordance —
          // skipping the last card would empty the active view, and the
          // deck's "skipped card cycles to the back" behavior is meaningless
          // with a single card. Computed from the post-exit live count so a
          // skip-in-progress that leaves one survivor immediately disables
          // skipping on that survivor.
          const isSoleLiveCard = liveTotal === 1;
          return cards.map((card) => {
            const isExiting =
              card.status.kind === "exit-skip" || card.status.kind === "exit-complete";
            const index = isExiting ? 0 : liveCounter++;
            const total = isExiting ? liveTotal + 1 : liveTotal;
            return (
              <DeckCard
                key={card.id}
                card={card}
                index={index}
                total={total}
                viewMode={viewMode}
                onSkip={skipAndAdvanceTour}
                canSkip={!isSoleLiveCard}
                onComplete={complete}
                onRecycle={recycle}
                beginSwipePending={beginSwipePending}
                resolveSwipe={resolveSwipe}
                onExitAnimationComplete={onExitAnimationComplete}
              />
            );
          });
        })()}

        {/*
         * Position cue. Skipping recycles a card to the back rather than
         * consuming it, so without a moving number the deck reads as an
         * infinite loop.
         *
         * It rides an empty, card-sized, non-interactive box rather than
         * sitting at the bottom of the deck container: the container is
         * `flex-1` and taller than the card, so anchoring to it would leave
         * the number floating in the slack below. Sharing
         * `STACK_CARD_SIZE_CLASS` with the card means `top-full` lands exactly
         * on the card's bottom edge at every breakpoint.
         *
         * Stack only — grid shows every card at once, so there's no position
         * to report.
         */}
        {viewMode === "stack" && passPosition > 0 ? (
          <div className={cn("pointer-events-none absolute mx-auto", STACK_CARD_SIZE_CLASS)}>
            <p
              aria-live="polite"
              className="absolute inset-x-0 top-full mt-3 text-center text-sm text-black/40"
            >
              {passPosition} of {liveCount}
            </p>
          </div>
        ) : null}
      </div>
    </LayoutGroup>
  );
}
